/// <reference types="@types/apple-mapkit-js-browser" />

const REQUIRED_LIBRARIES = ['map', 'annotations'] as const;

/**
 * Check if MapKit JS libraries have been loaded
 */
export function hasLoadedMapKitLibraries() {
  if (!globalThis.mapkit) {
    return false;
  }
  // @ts-expect-error: loadedLibraries not in @types/apple-mapkit-js-browser
  const { loadedLibraries } = globalThis.mapkit;
  if (!Array.isArray(loadedLibraries)) {
    return true;
  }
  return REQUIRED_LIBRARIES.every(lib => loadedLibraries.includes(lib));
}

/**
 * MapKit library loader with immutable promise pattern
 */
const mapKitLibraryLoader = {
  promise: null as Promise<void> | null,
  load(token: string): Promise<void> {
    // If already loaded, return resolved promise
    if (hasLoadedMapKitLibraries()) {
      if (!this.promise) {
        this.promise = Promise.resolve();
      }
      return this.promise;
    }

    // If promise already exists, return it
    if (this.promise) {
      return this.promise;
    }

    // Create new promise for loading
    this.promise = new Promise<void>((resolve, reject) => {
      const script = globalThis.document.createElement('script');
      script.addEventListener(
        'load',
        () => {
          globalThis.mapkit.addEventListener('error', reject);
          // @ts-expect-error: untyped event
          globalThis.mapkit.addEventListener('load-error', reject);

          globalThis.mapkit.addEventListener('configuration-change', () => {
            if (hasLoadedMapKitLibraries()) {
              resolve();
            }
          });
          // @ts-expect-error: untyped event
          globalThis.mapkit.addEventListener('load', () => {
            if (hasLoadedMapKitLibraries()) {
              resolve();
            }
          });

          globalThis.mapkit.init({
            authorizationCallback: done => done(token),
            libraries: REQUIRED_LIBRARIES,
          });
        },
        {
          once: true,
        }
      );
      script.addEventListener('error', reject, { once: true });
      script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js';
      script.crossOrigin = 'anonymous';
      globalThis.document.head.appendChild(script);
    });

    // Allow retry on failure: clear cached promise so next load() gets a fresh attempt
    this.promise.catch(() => {
      this.promise = null;
    });

    return this.promise;
  },
};

/**
 * Load Apple MapKit JS library
 */
export async function loadMapKitJSLibrary(token: string): Promise<void> {
  return await mapKitLibraryLoader.load(token);
}
